import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Eye, Search, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  CustomerAccount,
  deleteAdminCustomerAccount,
  fetchAdminCustomerAccounts,
  fetchAdminProjects,
  ProjectStatus,
  ProjectSummary,
  WorkflowApiError,
} from "../services/workflow";
import {
  formatCurrency,
  formatDateTime,
  getProjectStatusMeta,
} from "../services/workflow-ui";
import { useAuth } from "../contexts/AuthContext";

type StatusFilter = "all" | ProjectStatus;

const DELETE_CONFIRMATION_STEPS = [
  {
    title: "Kundenkonto wirklich loeschen?",
    description:
      "Dadurch wird das Kundenkonto aus dem Backend entfernt. Dieser Schritt ist bewusst zusaetzlich abgesichert.",
    actionLabel: "Ja, weiter",
  },
  {
    title: "Wirklich fortfahren?",
    description:
      "Alle zugeordneten Projekte und Workflow-Daten dieses Kunden werden ebenfalls geloescht.",
    actionLabel: "Ja, weiter",
  },
  {
    title: "Letzte Bestaetigung",
    description:
      "Dieser Vorgang kann nicht rueckgaengig gemacht werden. Bitte nur fortfahren, wenn die Loeschung wirklich beabsichtigt ist.",
    actionLabel: "Ja, wirklich loeschen",
  },
] as const;

export function AdminCustomers() {
  const navigate = useNavigate();
  const { impersonateCustomer } = useAuth();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [accounts, setAccounts] = useState<CustomerAccount[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [impersonatingUserId, setImpersonatingUserId] = useState("");
  const [deletingUserId, setDeletingUserId] = useState("");
  const [pendingDeletionAccount, setPendingDeletionAccount] = useState<CustomerAccount | null>(
    null,
  );
  const [deleteConfirmationStep, setDeleteConfirmationStep] = useState(0);

  const loadData = async () => {
    const [nextProjects, nextAccounts] = await Promise.all([
      fetchAdminProjects(),
      fetchAdminCustomerAccounts(),
    ]);

    setProjects(nextProjects);
    setAccounts(nextAccounts);
  };

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      try {
        const [nextProjects, nextAccounts] = await Promise.all([
          fetchAdminProjects(),
          fetchAdminCustomerAccounts(),
        ]);

        if (isMounted) {
          setProjects(nextProjects);
          setAccounts(nextAccounts);
        }
      } catch (loadError) {
        if (isMounted) {
          setError(
            loadError instanceof WorkflowApiError
              ? loadError.message
              : "Daten konnten nicht geladen werden",
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, []);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const haystack = [
        project.title,
        project.customer.name,
        project.customer.email,
        project.customer.company,
        project.questionnaire.contactCompany,
        project.questionnaire.contactPhone,
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch = haystack.includes(normalizedSearch);
      const matchesStatus = statusFilter === "all" || project.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [normalizedSearch, projects, searchQuery, statusFilter]);

  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      const haystack = [account.name, account.email, account.company].join(" ").toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [accounts, normalizedSearch]);

  const handleDeleteAccount = (account: CustomerAccount) => {
    setPendingDeletionAccount(account);
    setDeleteConfirmationStep(0);
  };

  const handleOpenCustomerView = async (account: CustomerAccount) => {
    setImpersonatingUserId(account.id);
    setError("");
    setSuccessMessage("");

    try {
      await impersonateCustomer(account.id);
      navigate("/dashboard");
    } catch (impersonationError) {
      setError(
        impersonationError instanceof WorkflowApiError
          ? impersonationError.message
          : impersonationError instanceof Error && impersonationError.message
            ? impersonationError.message
            : "Kundenansicht konnte nicht geöffnet werden",
      );
    } finally {
      setImpersonatingUserId("");
    }
  };

  const resetDeleteDialog = () => {
    if (deletingUserId) {
      return;
    }

    setPendingDeletionAccount(null);
    setDeleteConfirmationStep(0);
  };

  const handleDeleteConfirmation = async () => {
    if (!pendingDeletionAccount) {
      return;
    }

    if (deleteConfirmationStep < DELETE_CONFIRMATION_STEPS.length - 1) {
      setDeleteConfirmationStep((currentStep) => currentStep + 1);
      return;
    }

    setDeletingUserId(pendingDeletionAccount.id);
    setError("");
    setSuccessMessage("");

    try {
      const deletedAccount = await deleteAdminCustomerAccount(pendingDeletionAccount.id);
      await loadData();
      setSuccessMessage(`Kundenkonto ${deletedAccount.email} wurde geloescht`);
      setPendingDeletionAccount(null);
      setDeleteConfirmationStep(0);
    } catch (deleteError) {
      setError(
        deleteError instanceof WorkflowApiError
          ? deleteError.message
          : "Kundenkonto konnte nicht geloescht werden",
      );
    } finally {
      setDeletingUserId("");
    }
  };

  const currentDeleteStep = DELETE_CONFIRMATION_STEPS[deleteConfirmationStep];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Projekte & Kunden</h1>
        <p className="text-gray-600">
          Projekte verwalten und Kundenkonten bei Bedarf direkt im Backend entfernen.
        </p>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {successMessage}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Suche nach Projekt, Name, Firma oder E-Mail"
              className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-4 focus:border-black focus:outline-none"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            className="rounded-xl border border-gray-300 px-4 py-2.5 focus:border-black focus:outline-none"
          >
            <option value="all">Alle Status</option>
            <option value="questionnaire_received">Neue Anfrage</option>
            <option value="draft_ready">Entwurf bereit</option>
            <option value="draft_sent">Beim Kunden</option>
            <option value="revision_requested">Revision offen</option>
            <option value="assets_pending">Wartet auf Daten</option>
            <option value="finalizing">Finalisierung</option>
            <option value="live">Live</option>
          </select>
        </div>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[1.6fr_1.1fr_0.9fr_0.8fr_0.9fr] gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <span>Projekt</span>
          <span>Kunde</span>
          <span>Status</span>
          <span>Entwurf / Revision</span>
          <span className="text-right">Aktion</span>
        </div>

        {isLoading ? (
          <div className="px-6 py-10 text-sm text-gray-500">Projekte werden geladen...</div>
        ) : null}

        {!isLoading && filteredProjects.length === 0 ? (
          <div className="px-6 py-10 text-sm text-gray-500">
            Keine Projekte für den aktuellen Filter gefunden.
          </div>
        ) : null}

        <div className="divide-y divide-gray-100">
          {filteredProjects.map((project) => {
            const statusMeta = getProjectStatusMeta(project.status);

            return (
              <div
                key={project.id}
                className="grid grid-cols-1 gap-4 px-6 py-5 lg:grid-cols-[1.6fr_1.1fr_0.9fr_0.8fr_0.9fr] lg:items-center"
              >
                <div>
                  <p className="font-semibold text-gray-900">{project.title}</p>
                  <p className="mt-1 text-sm text-gray-600">
                    Erstellt am {formatDateTime(project.createdAt)}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Paket: {formatCurrency(project.pricing.setup)} +{" "}
                    {formatCurrency(project.pricing.monthly)} / Monat
                  </p>
                </div>

                <div>
                  <p className="font-medium text-gray-900">{project.customer.name}</p>
                  <p className="text-sm text-gray-600">{project.customer.company || "-"}</p>
                  <p className="text-xs text-gray-500">{project.customer.email}</p>
                  <p className="text-xs text-gray-500">
                    {project.questionnaire.contactPhone || "-"}
                  </p>
                </div>

                <div>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusMeta.className}`}>
                    {statusMeta.label}
                  </span>
                  <p className="mt-2 text-xs leading-relaxed text-gray-500">
                    {statusMeta.description}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {project.currentDraft
                      ? `V${project.currentDraft.version}: ${project.currentDraft.title}`
                      : "Noch kein Entwurf"}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Revisionen: {project.revisions.used}/{project.revisions.included}
                  </p>
                  <p className="text-xs text-gray-500">Uploads: {project.assetCount}</p>
                </div>

                <div className="flex justify-end">
                  <Link
                    to={`/admin/projects/${project.id}`}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900 transition hover:bg-gray-50"
                  >
                    Oeffnen
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Kundenkonten</h2>
          <p className="mt-1 text-sm text-gray-600">
            Hier kannst du Konten wie `kontakt@mutzur.de` direkt loeschen.
          </p>
        </div>

        {isLoading ? (
          <div className="px-6 py-10 text-sm text-gray-500">Kundenkonten werden geladen...</div>
        ) : null}

        {!isLoading && filteredAccounts.length === 0 ? (
          <div className="px-6 py-10 text-sm text-gray-500">
            Keine Kundenkonten für die aktuelle Suche gefunden.
          </div>
        ) : null}

        <div className="divide-y divide-gray-100">
          {filteredAccounts.map((account) => (
            <div
              key={account.id}
              className="flex flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center lg:justify-between"
            >
              <div>
                <p className="font-medium text-gray-900">{account.name}</p>
                <p className="text-sm text-gray-600">{account.company || "-"}</p>
                <p className="text-sm text-gray-600">{account.email}</p>
                <p className="mt-1 text-xs text-gray-500">
                  Projekte: {account.projectCount} · Erstellt: {formatDateTime(account.createdAt)}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    void handleOpenCustomerView(account);
                  }}
                  disabled={impersonatingUserId === account.id || !!deletingUserId}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Eye size={16} />
                  {impersonatingUserId === account.id ? "Oeffnet..." : "Kundenansicht"}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteAccount(account)}
                  disabled={deletingUserId === account.id || !!impersonatingUserId}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Trash2 size={16} />
                  {deletingUserId === account.id ? "Loescht..." : "Konto loeschen"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <AlertDialog
        open={!!pendingDeletionAccount}
        onOpenChange={(open) => !open && resetDeleteDialog()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{currentDeleteStep.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {currentDeleteStep.description}
              {pendingDeletionAccount ? (
                <>
                  {" "}
                  Betroffen ist{" "}
                  <span className="font-medium text-gray-900">
                    {pendingDeletionAccount.email}
                  </span>
                  .
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingUserId}>Abbrechen</AlertDialogCancel>
            <button
              type="button"
              onClick={handleDeleteConfirmation}
              disabled={!pendingDeletionAccount || !!deletingUserId}
              className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deletingUserId ? "Loescht..." : currentDeleteStep.actionLabel}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
